/**
 * TypeScript types for the FishMemory API
 */

// ─── User ───
export interface User {
  waId: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  lang: string;
  tz: string;
  timezone: string | null;
  trialUsed: number;
  trialEndUtc: string | null;
  subStatus: 'unsubscribed' | 'active' | 'expired' | 'trial';
  effectiveSubStatus?: 'unsubscribed' | 'active' | 'expired' | 'trial' | 'paused' | 'blocked' | 'canceled';
  customerLifecycle?: 'trial_active' | 'trial_expired' | 'paid_active' | 'paid_expired' | 'paid_inactive' | 'paid_blocked' | 'blocked' | 'not_subscribed';
  hasPaidBefore?: boolean;
  paidOrderCount?: number;
  subUntilUtc: string | null;
  autosyncGoogle: boolean;
  headsupEnabled: boolean;
  headsupMinutes: number;
  defaultDurationMinutes: number;
  defaultReminderTimeMinutes: number;
  tagColors: Record<string, string> | null;
  scheduledDeletionAt: string | null;
}

// ─── Auth ───
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthResponse extends AuthTokens {
  user: User;
}

// ─── Chat ───
export interface ChatMessage {
  id: number;
  wa_id: string;
  direction: 'inbound' | 'outbound';
  channel: 'whatsapp' | 'app' | 'server';
  msg_type: 'text' | 'interactive_buttons' | 'interactive_list' | 'document' | 'template' | 'button' | 'list_select';
  body: string;
  metadata: string | null;
  created_at_utc: string;
  status?: 'sending' | 'sent' | 'failed';
  clientMsgId?: string;
  reactions?: { wa_id: string; emoji: string }[];
}

export interface WsMessagePayload {
  id: string;
  msgType: 'text' | 'interactive_buttons' | 'interactive_list' | 'document';
  body: string;
  footer?: string | null;
  buttons?: { id: string; title: string }[];
  header?: string | null;
  buttonText?: string;
  sections?: any[];
  link?: string;
  filename?: string;
  timestamp: string;
}

export interface WsMessage {
  type: 'message' | 'typing' | 'connected' | 'pong' | 'sync' | 'error' | 'reminder_fired';
  payload: any;
}

// ─── Reminder (raw DB row from /api/v1/reminders) ───
export interface Reminder {
  id: number;
  wa_id: string;
  user_seq: number;
  body: string;
  tags: string | null;
  run_at_utc: string;
  created_at_utc: string;
  sent_at_utc: string | null;
  is_recurring: number;
  recur_json: string | null;
  until_utc: string | null;
  is_paused: number;
  parent_id: number | null;
  kind: string;
  google_event_id: string | null;
  // 'app' (created in FishMemory) | 'google' (imported from Google Calendar).
  source?: 'app' | 'google';
  fire_count?: number;
  last_done_state_json?: string | null;
  is_done: number;
  duration_minutes?: number | null;
  col_pref?: number | null;
  // §24: Enriched by API
  classification?: 'overdue' | 'today' | 'upcoming' | 'paused' | 'done';
  next_run_utc?: string | null;
}

// Computed helper
export type ReminderStatus = 'active' | 'snoozed' | 'paused' | 'done';

export function getReminderStatus(r: Reminder): ReminderStatus {
  if (r.is_paused) return 'paused';
  if (r.is_done) return 'done';
  return 'active';
}

// ─── Calendar (transformed by API) ───
export interface CalendarEvent {
  type: 'reminder' | 'google';
  seq?: number;
  body: string;
  runAtUtc: string;
  isRecurring: boolean;
  isPaused?: boolean;
  sent?: boolean;
  tags?: string | null;
  durationMinutes?: number | null;
  colPref?: number | null;
  recurJson?: string | null;
  untilUtc?: string | null;
  // V10: set when this reminder is synced to Google Calendar, so the
  // calendar cell can show the blue Google dot indicator.
  googleEventId?: string | null;
  // V10 Phase 2: 'app' = synced TO Google (blue G), 'google' = imported
  // FROM Google (red G).
  source?: 'app' | 'google';
}

// ─── API Responses ───
export interface ApiResponse<T> {
  data: T;
  error?: string;
}

export interface RemindersListResponse {
  reminders: Reminder[];
}

export interface ChatHistoryResponse {
  messages: ChatMessage[];
}

export interface ChatCommandResponse {
  success: boolean;
  messages: WsMessagePayload[];
}

export interface CalendarEventsResponse {
  events: CalendarEvent[];
  googleEvents?: any[];
}

export interface TodayResponse {
  events: CalendarEvent[];
  completionRate: number;
}

// ─── Payments ───
export interface SubscriptionBundle {
  id: number;
  label: string;
  priceUsd: number;
  durationMonths: number;
}

export interface PaymentProviderStatus {
  provider: string;
  enabled: boolean;
  mode: string;
  manualFallback: boolean;
  /** Optional sibling gateway. Currently only Whish Pay (Lebanon wallet). */
  whish?: {
    enabled: boolean;
    env?: 'sandbox' | 'production' | string;
  };
}

export interface PaymentOrder {
  id: string;
  bundleId: number;
  bundleLabel: string;
  amountUsd: number;
  currency: string;
  durationMonths: number;
  status: string;
  clientReferenceCode: string;
  createdAtUtc: string;
  completedAtUtc: string | null;
}

export interface SubscriptionBundlesResponse {
  bundles: SubscriptionBundle[];
  payments: PaymentProviderStatus;
}

export interface PaymentOrderResponse {
  order: PaymentOrder;
  payments?: PaymentProviderStatus;
}

export interface PaymentCaptureContextResponse {
  order: PaymentOrder;
  sessionJwt: string;
  clientLibraryUrl: string | null;
  expiresAtUtc: string | null;
}

export interface PaymentCompletionResponse {
  order: PaymentOrder;
  status: string;
  subUntilUtc: string | null;
  user?: User;
}

export interface PublicSubscriptionStartResponse {
  success: boolean;
  phone: string;
  isTestAccount?: boolean;
}

export interface PublicSubscriptionVerifyResponse {
  verified: boolean;
  token: string;
  expiresAtUtc: string;
  user: User;
}

export interface PublicSubscriptionCheckoutIntentResponse {
  token: string;
  checkoutUrl: string;
  expiresAtUtc: string;
  bundle: SubscriptionBundle;
  payments?: PaymentProviderStatus;
}

// ─── Subscription auto-renewal ───
export type RenewalStatus = 'none' | 'scheduled' | 'past_due' | 'cancelled';

export interface SubscriptionCard {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface SubscriptionRenewalBundle {
  id: number;
  label: string;
  priceUsd: number;
  durationMonths: number;
}

export interface SubscriptionDetails {
  subUntilUtc: string | null;
  subStatus: 'unsubscribed' | 'active' | 'expired' | 'trial';
  autoRenew: boolean;
  renewalStatus: RenewalStatus;
  nextRenewalAtUtc: string | null;
  renewalFailedAttemptCount: number;
  card: SubscriptionCard | null;
  renewalBundle: SubscriptionRenewalBundle | null;
}

export interface SubscriptionDetailsResponse {
  subscription: SubscriptionDetails;
  payments?: any;
}

export interface AutoRenewResponse {
  subUntilUtc: string | null;
  autoRenew: boolean;
  nextRenewalAtUtc?: string | null;
  subscription: SubscriptionDetails;
}

export interface CancelSubscriptionResponse {
  subUntilUtc: string | null;
  autoRenew: false;
  subscription: SubscriptionDetails;
}

export interface CardUpdateCompletionResponse {
  card: SubscriptionCard;
  subscription: SubscriptionDetails;
}
